import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { isMarketOpen, sessionAsOf } from '../utils/marketCalendar';
import { fetchMarketBreadth } from '../services/marketBreadthService';
import { PanelLoading, PanelUnavailable } from './PanelMessage';

/**
 * Advancers and decliners across the whole tape, bucketed by size of move.
 *
 * Drawn as a tornado: bands down the centre, decliners left, advancers right,
 * on one shared scale so a bar on either side means the same thing.
 *
 * The universe count is deliberately NOT shown. It is still on the payload as
 * `universe` and is the first thing to reach for if these figures ever need
 * reconciling against another screen.
 *
 * The component makes no assumption about how many bands it is given — send
 * four or seven and it renders either.
 */

const REFRESH_MS = 5 * 60 * 1000;

/* ── chrome ─────────────────────────────────────────────────────────────── */

const fmtCount = (n: number) => n.toLocaleString('en-US');

/** Band label riding the centre line. The hairlines form the axis. */
const CENTRE_LABEL =
  'w-14 flex-none self-stretch flex items-center justify-center border-x border-dark-border ' +
  'font-numeric text-[10px] text-gray-200';

function Stat({ value, label, tone }: {
  value: string;
  label: string;
  tone: string;
}): React.ReactElement {
  return (
    <div className="min-w-0 px-2 text-center">
      <div className={`font-numeric text-xl font-bold leading-none ${tone}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-gray-400 mt-1 truncate">{label}</div>
    </div>
  );
}

/** Half a row: the bar anchored at the axis, its count on the outer side. */
function Wing({ count, max, side }: {
  count: number;
  max: number;
  side: 'down' | 'up';
}): React.ReactElement {
  const down = side === 'down';
  // A floor of 2% keeps a small-but-nonzero band visible; without it a handful
  // of names reads as none at all.
  const width = `${Math.max((count / max) * 100, count ? 2 : 0)}%`;

  return (
    <div className={`flex-1 min-w-0 flex items-center gap-2 ${down ? 'flex-row-reverse' : ''}`}>
      <div
        className={`h-2.5 rounded-sm ${down ? 'bg-bear-red' : 'bg-bull-blue'}`}
        style={{ width, opacity: 0.9 }}
      />
      <span
        className={`font-numeric text-[10px] tabular-nums flex-none ${
          down ? 'text-bear-red' : 'text-bull-blue'
        }`}
      >
        {fmtCount(count)}
      </span>
    </div>
  );
}

/** The card shell, so the loading and error states are ruled like the data one. */
function BreadthCard({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <section className="p-4 rounded-lg border border-dark-border bg-dark-surface flex flex-col">
      <div className="flex items-baseline justify-center gap-3 flex-wrap pb-2 mb-3 border-b border-dark-border">
        <h2 className="text-sm font-bold text-white uppercase tracking-[1.5px]">
          Advancers + Decliners
        </h2>
      </div>
      {children}
    </section>
  );
}

/* ── component ──────────────────────────────────────────────────────────── */

export function MarketBreadth(): React.ReactElement {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['marketBreadth'],
    queryFn: fetchMarketBreadth,
    staleTime: REFRESH_MS,
    // Polls only while the session is open. Breadth cannot change once the
    // bell has gone, so refetching overnight would spend requests to receive
    // the same numbers back.
    refetchInterval: () => (isMarketOpen() ? REFRESH_MS : false),
    // One focus-refetch is still right after the bell — a tab left open since
    // 15:50 holds a mid-session count, and coming back to it is exactly when
    // someone wants the final figure. But only until we hold the close: from
    // then nothing changes until 09:30.
    //
    refetchOnWindowFocus: (query) => {
      if (isMarketOpen()) return true;
      const held = query.state.data?.capturedAt;
      return !held || held < sessionAsOf().asOf;
    },
  });

  if (isLoading) {
    return (
      <BreadthCard>
        <PanelLoading />
      </BreadthCard>
    );
  }

  if (isError || !data) {
    return (
      <BreadthCard>
        <PanelUnavailable what="Market breadth" />
      </BreadthCard>
    );
  }

  const { bands, unchanged } = data;
  const decliners = bands.reduce((n, b) => n + b.down, 0);
  const advancers = bands.reduce((n, b) => n + b.up, 0);

  // Shares are of the whole universe, flat included, so the three add to 100.
  const universe = decliners + advancers + unchanged || 1;
  const pct = (n: number) => `${((n / universe) * 100).toFixed(1)}%`;

  // One scale across both sides, so a bar on the left and a bar on the right
  // mean the same thing. Per-side scaling would make a light up-day look as
  // broad as a heavy down-day.
  const max = Math.max(...bands.flatMap((b) => [b.down, b.up]), 1);

  // Guard the divide: a session with no decliners at all is unlikely, but the
  // ratio would come out Infinity rather than merely large.
  const ratio = decliners ? advancers / decliners : null;

  return (
    <BreadthCard>
      <div className="grid grid-cols-4 divide-x divide-dark-border">
        <Stat
          value={ratio == null ? '—' : ratio.toFixed(2)}
          label="A/D ratio"
          tone={ratio != null && ratio >= 1 ? 'text-bull-blue' : 'text-bear-red'}
        />
        <Stat value={pct(decliners)} label="Down" tone="text-bear-red" />
        <Stat value={pct(unchanged)} label="Flat" tone="text-gray-300" />
        <Stat value={pct(advancers)} label="Up" tone="text-bull-blue" />
      </div>

      <div className="flex items-baseline text-[9px] uppercase tracking-wider text-gray-400 mt-4">
        <span className="flex-1 text-right pr-2">&#9664; Down</span>
        <span className="w-14 flex-none" />
        <span className="flex-1 pl-2">Up &#9654;</span>
      </div>

      {/* Rows carry their spacing as padding rather than a gap, so the centre
          hairlines butt up into one continuous axis instead of a dashed one. */}
      <div className="flex-1 min-h-0 flex flex-col justify-center mt-1">
        {bands.map((b) => (
          <div key={b.label} className="flex items-center py-1">
            <Wing count={b.down} max={max} side="down" />
            <span className={CENTRE_LABEL}>{b.label}</span>
            <Wing count={b.up} max={max} side="up" />
          </div>
        ))}

        {/* Totals as the last row of the tornado rather than a footer, so each
            side stays on its own side of the axis. No bars: the totals dwarf
            any single band, and drawing them on the same scale would either
            blow it out or need a second one that means something else. */}
        <div className="flex items-stretch border-t border-dark-border mt-1 pt-1">
          <div className="flex-1 min-w-0 flex items-center justify-end pr-2">
            <span className="font-numeric text-xs font-semibold text-bear-red">
              {fmtCount(decliners)}
            </span>
          </div>
          <span
            className={`${CENTRE_LABEL} uppercase tracking-wider text-gray-400`}
            title={`Decliners: ${fmtCount(decliners)} — Advancers: ${fmtCount(advancers)}`}
          >
            Total
          </span>
          <div className="flex-1 min-w-0 flex items-center pl-2">
            <span className="font-numeric text-xs font-semibold text-bull-blue">
              {fmtCount(advancers)}
            </span>
          </div>
        </div>
      </div>
    </BreadthCard>
  );
}
